/**
 * @kantera/config
 * Shared configuration for Kantera applications — Zod-validated, production-grade.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------
/**
 * The sender when POSTMARK_FROM_EMAIL is unset (KAN-218).
 *
 * It was the no-reply address on the .com that shares Kantera's name, which belongs to someone
 * else (an unrelated site with its own mail): an auto-reply or a reply to the From address went to
 * a stranger. kantera.dev is Kantera's own domain and the one its Postmark server sends from
 * (docs/ops-guide/supabase-auth-emails.md). Replies go to support@ in any case: sendEmail sets
 * Reply-To (apps/api/src/services/email.ts).
 *
 * A default, not a boot failure, on purpose: the config's style is to fail loudly only for the
 * secrets a production server cannot run without (assertProductionSecrets), and a sender is not a
 * secret. With no POSTMARK_API_TOKEN nothing is sent at all, and when one is set, a missing
 * POSTMARK_FROM_EMAIL now falls back to Kantera's own domain instead of taking the API down. A
 * value set in POSTMARK_FROM_EMAIL is used exactly as before.
 */
export const DEFAULT_POSTMARK_FROM_EMAIL = 'noreply@kantera.dev';
export const AppConfigSchema = z.object({
    env: z.enum(['development', 'test', 'staging', 'uat', 'production']),
    // Internationalization
    defaultLocale: z.enum(['en', 'es', 'fr', 'pt', 'de']).default('en'),
    supportedLocales: z.array(z.string()).default(['en', 'es']),
    // API
    apiUrl: z.string().url(),
    /**
     * Public web-app origin (Cloudflare Pages) — where the user-facing pages that
     * serve invitation/consent flows live. Clickable email links MUST be built on
     * this origin, NOT `apiUrl` (the API origin has no HTML page at those paths and
     * 404s). Kept a sibling of `apiUrl` so both loaders set them side by side.
     */
    webUrl: z.string().url(),
    apiPort: z.number().int().min(1).max(65535).default(3000),
    apiHost: z.string().default('0.0.0.0'),
    // Supabase
    supabaseUrl: z.string().url(),
    supabaseAnonKey: z.string().min(1),
    supabaseServiceKey: z.string().min(1).optional(),
    // Database
    databaseUrl: z.string().optional(),
    dbPoolMin: z.number().int().default(2),
    dbPoolMax: z.number().int().default(10),
    // Auth
    jwtSecret: z.string().min(32).optional(),
    sessionTimeoutMinutes: z.number().int().default(60),
    // CORS
    corsOrigins: z.array(z.string()).default(['http://localhost:5173']),
    // Rate limiting
    rateLimitMax: z.number().int().default(100),
    rateLimitWindowMs: z.number().int().default(60000),
    // Logging
    logLevel: z.enum(['debug', 'info', 'warn', 'error']).default('info'),
    // Storage (Cloudflare R2)
    r2AccountId: z.string().optional(),
    r2AccessKeyId: z.string().optional(),
    r2SecretAccessKey: z.string().optional(),
    r2BucketName: z.string().default('kantera-uploads'),
    /**
     * R2 jurisdiction segment for the S3 endpoint. Cloudflare accounts in a
     * data-locality jurisdiction (e.g. `eu`, `fedramp`) must address R2 as
     * `{account}.{jurisdiction}.r2.cloudflarestorage.com`; the plain host
     * fails the TLS handshake. Empty/unset = default (no segment).
     */
    r2Jurisdiction: z.string().optional(),
    /** Public CDN base URL for assets in the public bucket (no trailing slash). */
    r2PublicUrl: z.string().optional(),
    /**
     * Server-Side Encryption with Customer-provided keys (SSE-C) key —
     * KI-041 defense-in-depth (2026-04-29). 32-byte AES-256 key,
     * base64-encoded. R2 always encrypts at rest with its own AES-256
     * (Cloudflare-managed); SSE-C layers a customer-managed key on top
     * for server-side PUTs/GETs (highlight renders, transcoded clips,
     * jersey crops, exports). Presigned URLs deliberately don't use
     * SSE-C — see `apps/api/src/services/storage.ts` for the rationale.
     *
     * If unset, server-side flows fall back to R2's default-only
     * encryption (the long-standing behaviour pre-KI-041). The wiring
     * is opt-in so missing-key in dev/CI doesn't break uploads.
     */
    r2SseCKey: z.string().optional(),
    // Email (Postmark)
    postmarkApiToken: z.string().optional(),
    postmarkFromEmail: z.string().email().default(DEFAULT_POSTMARK_FROM_EMAIL),
    /**
     * Shared secret for POST /api/webhooks/postmark. Postmark authenticates
     * itself with this — either as HTTP Basic auth baked into the webhook URL
     * configured in the Postmark dashboard, or as an X-Postmark-Webhook-Secret
     * header. Unset means the endpoint refuses every callback (503), so delivery
     * status stays at 'sent' rather than silently accepting forged updates.
     */
    postmarkWebhookSecret: z.string().optional(),
    // Push notifications (Expo)
    expoPushAccessToken: z.string().optional(),
    // Web Push (VAPID)
    /** VAPID public key — safe to expose to the browser; the service worker needs it to subscribe. */
    vapidPublicKey: z.string().optional(),
    /** VAPID private key — server-only, never expose to the browser. */
    vapidPrivateKey: z.string().optional(),
    /** VAPID subject — abuse contact for push services; must be a `mailto:` URI or https URL. */
    vapidSubject: z.string().optional(),
    // SMS (Twilio) — match-day reminders, two-factor, urgent parent notifications
    twilioAccountSid: z.string().optional(),
    twilioAuthToken: z.string().optional(),
    /** Twilio phone number to send SMS from, in E.164 format (e.g. +15551234567). */
    twilioPhoneNumber: z.string().optional(),
    // Weather (OpenWeatherMap) — match-day weather, field-condition risk scoring
    openWeatherApiKey: z.string().optional(),
    // AI narrative — Anthropic Claude (player development report generation)
    anthropicApiKey: z.string().optional(),
    /**
     * Claude model identifier used by the report-generator worker.
     * Defaults to the latest stable Sonnet; override via ANTHROPIC_MODEL env.
     */
    anthropicModel: z.string().default('claude-sonnet-4-6'),
    // AI inference — Replicate (YOLO11 detection, avatar generation)
    replicateApiToken: z.string().optional(),
    /**
     * Replicate model version for YOLO11 object detection. Default is the
     * `lucataco/yolo11` community model pinned to a known-good version hash.
     * Override REPLICATE_YOLO11_MODEL_VERSION in env to pin a different build.
     */
    replicateYolo11ModelVersion: z
        .string()
        .default('lucataco/yolo11:cc14833a98e8a58cc51ff2bb48c69c36e2b3ad40b90af5cfbf7a2c5b3bce4c22'),
    /**
     * Replicate model version for PaddleOCR (jersey-number extraction).
     * Pinned to the `cjwbw/paddleocr` community model. Override via
     * REPLICATE_PADDLEOCR_MODEL_VERSION in env. The crop → OCR pipeline
     * is invoked from the analysis worker only when YOLO has cropped
     * out a candidate player region.
     */
    replicatePaddleocrModelVersion: z
        .string()
        .default('cjwbw/paddleocr:30f9069e56763e9fd43d67afac0a34e7c68e9b6b7f5d9f5e0c98f5c87c06ef5d'),
    /**
     * Replicate model for AI avatar generation (FLUX 1.1 Pro).
     * Default: 'black-forest-labs/flux-1.1-pro'. Override via
     * REPLICATE_AVATAR_MODEL env to pin a different model or version.
     */
    replicateAvatarModel: z.string().default('black-forest-labs/flux-1.1-pro'),
    // Feature flags
    featureVideoAnalysis: z.boolean().default(false),
    featureLiveScoring: z.boolean().default(false),
    featureGamification: z.boolean().default(true),
    featureOfflineSync: z.boolean().default(false),
    // Upload limits
    maxUploadSizeMb: z.number().default(50),
    allowedImageTypes: z.array(z.string()).default(['image/jpeg', 'image/png', 'image/webp']),
    allowedVideoTypes: z.array(z.string()).default(['video/mp4', 'video/quicktime']),
    // Pagination defaults
    defaultPageSize: z.number().int().default(20),
    maxPageSize: z.number().int().default(100),
    // Event-store snapshots
    snapshotThreshold: z.number().int().default(50),
    // Club defaults
    defaultMaxTeams: z.number().int().default(10),
    defaultMaxPlayers: z.number().int().default(200),
    // Age verification
    ageVerificationExpiryDays: z.number().int().default(730), // 2 years
    // Sentry
    sentryDsn: z.string().url().optional(),
    sentryEnvironment: z
        .enum(['development', 'test', 'staging', 'uat', 'production'])
        .default('development'),
    sentryTracesSampleRate: z.number().min(0).max(1).default(0.1),
    // Stripe
    stripeSecretKey: z.string().optional(),
    stripeWebhookSecret: z.string().optional(),
    stripePriceIds: z
        .object({
        community: z.string().default('price_community'),
        club: z.string().default('price_club'),
        academy: z.string().default('price_academy'),
        professional: z.string().default('price_professional'),
    })
        .prefault({}),
    // Remotion highlight renderer (P3). Single canonical URL var so the two render
    // paths (generate-highlight job + highlight-reel service) never disagree; both
    // read these fields instead of raw process.env. Optional → unset means the
    // highlight feature is disabled (no-op), matching prior behaviour.
    remotionRendererUrl: z.string().url().optional(),
    remotionRenderToken: z.string().optional(),
});
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/**
 * Parse a comma-separated env var into a string array.
 * Returns the supplied fallback when the variable is absent or blank.
 */
function parseStringArray(raw, fallback) {
    if (!raw || raw.trim() === '')
        return fallback;
    return raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
}
/** Coerce a string env var to a boolean.  "true" / "1" / "yes" → true. */
function parseBool(raw, fallback) {
    if (raw === undefined)
        return fallback;
    return ['true', '1', 'yes'].includes(raw.toLowerCase());
}
/** Coerce a string env var to an integer, returning the fallback on failure. */
function parseInt10(raw, fallback) {
    if (raw === undefined)
        return fallback;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : fallback;
}
/** Coerce a string env var to a float, returning the fallback on failure. */
function parseFloat10(raw, fallback) {
    if (raw === undefined)
        return fallback;
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : fallback;
}
// ---------------------------------------------------------------------------
// Validation helper
// ---------------------------------------------------------------------------
/**
 * Validates a raw config object against AppConfigSchema and throws a
 * detailed error listing every failing field if validation fails.
 */
export function validateConfig(raw) {
    const result = AppConfigSchema.safeParse(raw);
    if (result.success)
        return result.data;
    const issues = result.error.issues.map((issue) => {
        const path = issue.path.length > 0 ? issue.path.join('.') : '(root)';
        return `  • ${path}: ${issue.message}`;
    });
    throw new Error(`[@kantera/config] Invalid configuration — ${issues.length} error(s):\n${issues.join('\n')}`);
}
/**
 * Critical secrets that a production SERVER deployment cannot run without.
 * Tuple is [config key, originating env var name] so the error can name the
 * env var the operator must set.
 */
const PRODUCTION_REQUIRED_SECRETS = [
    ['databaseUrl', 'DATABASE_URL'],
    ['supabaseServiceKey', 'SUPABASE_SERVICE_KEY'],
    ['stripeSecretKey', 'STRIPE_SECRET_KEY'],
    ['r2AccessKeyId', 'R2_ACCESS_KEY_ID'],
    ['anthropicApiKey', 'ANTHROPIC_API_KEY'],
];
/**
 * OBS-2 (Law 9): a production SERVER boot must FAIL LOUDLY when a critical secret
 * is missing, rather than silently booting in a degraded state (Stripe/R2/AI/DB
 * features quietly broken). These keys are `.optional()` in the schema because
 * dev/CI legitimately run without them.
 *
 * This lives in the server loader on purpose — it is intentionally NOT a schema
 * `.superRefine`, because the browser loader (`loadWebConfig`) shares
 * `AppConfigSchema` and correctly has NO server secrets even in a production
 * (MODE=production) build. Enforcing them at the schema level would crash the
 * production web bundle.
 */
function assertProductionSecrets(raw) {
    if (raw['env'] !== 'production')
        return;
    const missing = PRODUCTION_REQUIRED_SECRETS.filter(([key]) => {
        const value = raw[key];
        return value === undefined || value === null || value === '';
    }).map(([, envVar]) => envVar);
    if (missing.length > 0) {
        throw new Error(`[@kantera/config] Missing required production secret(s): ${missing.join(', ')}. ` +
            'Set them via Fly.io secrets before deploying — Kantera will not boot in a ' +
            'degraded state (Law 9: no stubs / no silent fallback).');
    }
}
// ---------------------------------------------------------------------------
// Server-side loader  (uses process.env)
// ---------------------------------------------------------------------------
/**
 * Builds and validates the full application config from environment
 * variables.  Intended for server-side (API / Node.js) use only.
 * Throws with a detailed message when required values are missing or invalid.
 */
export const loadConfig = () => {
    const env = process.env;
    const raw = {
        env: env['NODE_ENV'] || 'development',
        // Internationalization
        defaultLocale: env['DEFAULT_LOCALE'] || 'en',
        supportedLocales: parseStringArray(env['SUPPORTED_LOCALES'], ['en', 'es']),
        // API
        apiUrl: env['API_URL'] || env['VITE_API_URL'] || 'http://localhost:3000',
        webUrl: env['WEB_URL'] || env['VITE_WEB_URL'] || 'http://localhost:5173',
        apiPort: parseInt10(env['API_PORT'], 3000),
        apiHost: env['API_HOST'] || '0.0.0.0',
        // Supabase — prefer server-side keys, fall back to VITE_ equivalents
        supabaseUrl: env['SUPABASE_URL'] || env['VITE_SUPABASE_URL'] || '',
        supabaseAnonKey: env['SUPABASE_ANON_KEY'] || env['VITE_SUPABASE_ANON_KEY'] || '',
        supabaseServiceKey: env['SUPABASE_SERVICE_KEY'],
        // Database
        databaseUrl: env['DATABASE_URL'],
        dbPoolMin: parseInt10(env['DB_POOL_MIN'], 2),
        dbPoolMax: parseInt10(env['DB_POOL_MAX'], 10),
        // Auth
        jwtSecret: env['JWT_SECRET'],
        sessionTimeoutMinutes: parseInt10(env['SESSION_TIMEOUT_MINUTES'], 60),
        // CORS
        corsOrigins: parseStringArray(env['CORS_ORIGINS'], ['http://localhost:5173']),
        // Rate limiting
        rateLimitMax: parseInt10(env['RATE_LIMIT_MAX'], 100),
        rateLimitWindowMs: parseInt10(env['RATE_LIMIT_WINDOW_MS'], 60000),
        // Logging
        logLevel: env['LOG_LEVEL'] || 'info',
        // Cloudflare R2
        r2AccountId: env['R2_ACCOUNT_ID'],
        r2AccessKeyId: env['R2_ACCESS_KEY_ID'],
        r2SecretAccessKey: env['R2_SECRET_ACCESS_KEY'],
        r2BucketName: env['R2_BUCKET_NAME'] || 'kantera-uploads',
        r2Jurisdiction: env['R2_JURISDICTION'],
        r2PublicUrl: env['R2_PUBLIC_URL'],
        r2SseCKey: env['R2_SSE_C_KEY'],
        // Postmark
        postmarkApiToken: env['POSTMARK_API_TOKEN'],
        postmarkFromEmail: env['POSTMARK_FROM_EMAIL'] || DEFAULT_POSTMARK_FROM_EMAIL,
        postmarkWebhookSecret: env['POSTMARK_WEBHOOK_SECRET'],
        // Expo Push
        expoPushAccessToken: env['EXPO_PUSH_ACCESS_TOKEN'],
        // Web Push (VAPID)
        vapidPublicKey: env['VAPID_PUBLIC_KEY'],
        vapidPrivateKey: env['VAPID_PRIVATE_KEY'],
        vapidSubject: env['VAPID_SUBJECT'],
        // Twilio SMS
        twilioAccountSid: env['TWILIO_ACCOUNT_SID'],
        twilioAuthToken: env['TWILIO_AUTH_TOKEN'],
        twilioPhoneNumber: env['TWILIO_PHONE_NUMBER'],
        // OpenWeatherMap
        openWeatherApiKey: env['OPENWEATHER_API_KEY'],
        // Anthropic (AI narrative generation)
        anthropicApiKey: env['ANTHROPIC_API_KEY'],
        anthropicModel: env['ANTHROPIC_MODEL'] || 'claude-sonnet-4-6',
        // Replicate (AI inference)
        replicateApiToken: env['REPLICATE_API_TOKEN'],
        replicateYolo11ModelVersion: env['REPLICATE_YOLO11_MODEL_VERSION'] ||
            'lucataco/yolo11:cc14833a98e8a58cc51ff2bb48c69c36e2b3ad40b90af5cfbf7a2c5b3bce4c22',
        replicatePaddleocrModelVersion: env['REPLICATE_PADDLEOCR_MODEL_VERSION'] ||
            'cjwbw/paddleocr:30f9069e56763e9fd43d67afac0a34e7c68e9b6b7f5d9f5e0c98f5c87c06ef5d',
        replicateAvatarModel: env['REPLICATE_AVATAR_MODEL'] || 'black-forest-labs/flux-1.1-pro',
        // Feature flags
        featureVideoAnalysis: parseBool(env['FEATURE_VIDEO_ANALYSIS'], false),
        featureLiveScoring: parseBool(env['FEATURE_LIVE_SCORING'], false),
        featureGamification: parseBool(env['FEATURE_GAMIFICATION'], true),
        featureOfflineSync: parseBool(env['FEATURE_OFFLINE_SYNC'], false),
        // Upload limits
        maxUploadSizeMb: parseFloat10(env['MAX_UPLOAD_SIZE_MB'], 50),
        allowedImageTypes: parseStringArray(env['ALLOWED_IMAGE_TYPES'], [
            'image/jpeg',
            'image/png',
            'image/webp',
        ]),
        allowedVideoTypes: parseStringArray(env['ALLOWED_VIDEO_TYPES'], [
            'video/mp4',
            'video/quicktime',
        ]),
        // Pagination
        defaultPageSize: parseInt10(env['DEFAULT_PAGE_SIZE'], 20),
        maxPageSize: parseInt10(env['MAX_PAGE_SIZE'], 100),
        // Event-store snapshots
        snapshotThreshold: parseInt10(env['SNAPSHOT_THRESHOLD'], 50),
        // Club defaults
        defaultMaxTeams: parseInt10(env['DEFAULT_MAX_TEAMS'], 10),
        defaultMaxPlayers: parseInt10(env['DEFAULT_MAX_PLAYERS'], 200),
        // Age verification
        ageVerificationExpiryDays: parseInt10(env['AGE_VERIFICATION_EXPIRY_DAYS'], 730),
        // Sentry
        sentryDsn: env['SENTRY_DSN'],
        sentryEnvironment: env['SENTRY_ENVIRONMENT'] || env['NODE_ENV'] || 'development',
        sentryTracesSampleRate: parseFloat10(env['SENTRY_TRACES_SAMPLE_RATE'], 0.1),
        // Stripe
        stripeSecretKey: env['STRIPE_SECRET_KEY'],
        stripeWebhookSecret: env['STRIPE_WEBHOOK_SECRET'],
        stripePriceIds: {
            community: env['STRIPE_PRICE_COMMUNITY'] ?? 'price_community',
            club: env['STRIPE_PRICE_CLUB'] ?? 'price_club',
            academy: env['STRIPE_PRICE_ACADEMY'] ?? 'price_academy',
            professional: env['STRIPE_PRICE_PROFESSIONAL'] ?? 'price_professional',
        },
        // Remotion highlight renderer (P3): REMOTION_RENDERER_URL is canonical;
        // REMOTION_WORKER_URL is accepted as a legacy alias so deployments that set
        // either name keep working. Both render paths read these fields, not env.
        remotionRendererUrl: env['REMOTION_RENDERER_URL'] ?? env['REMOTION_WORKER_URL'],
        remotionRenderToken: env['REMOTION_RENDER_TOKEN'],
    };
    // OBS-2: fail loud on a production server missing a critical secret (Law 9).
    // Runs BEFORE schema validation so the operator gets a precise, actionable
    // "set DATABASE_URL, STRIPE_SECRET_KEY" message rather than a generic parse error.
    assertProductionSecrets(raw);
    return validateConfig(raw);
};
// ---------------------------------------------------------------------------
// Browser / Vite loader  (uses import.meta.env)
// ---------------------------------------------------------------------------
/**
 * Builds and validates config from Vite's `import.meta.env`.
 * Only VITE_* prefixed variables are available in the browser bundle.
 * Intended for use inside React / web apps — do not call from Node.js.
 */
export const loadWebConfig = () => {
    const viteEnv = import.meta.env;
    const raw = {
        env: viteEnv['MODE'] || 'development',
        // Internationalization
        defaultLocale: viteEnv['VITE_DEFAULT_LOCALE'] || 'en',
        supportedLocales: parseStringArray(viteEnv['VITE_SUPPORTED_LOCALES'], ['en', 'es']),
        // API
        apiUrl: viteEnv['VITE_API_URL'] || 'http://localhost:3000',
        webUrl: viteEnv['VITE_WEB_URL'] || 'http://localhost:5173',
        apiPort: parseInt10(viteEnv['VITE_API_PORT'], 3000),
        apiHost: '0.0.0.0', // not meaningful in browser; satisfies schema
        // Supabase
        supabaseUrl: viteEnv['VITE_SUPABASE_URL'] || '',
        supabaseAnonKey: viteEnv['VITE_SUPABASE_ANON_KEY'] || '',
        // supabaseServiceKey intentionally omitted — never expose in browser
        // Database — not accessible in browser; satisfy schema with undefined
        databaseUrl: undefined,
        dbPoolMin: 2,
        dbPoolMax: 10,
        // Auth
        jwtSecret: undefined, // never expose in browser
        sessionTimeoutMinutes: parseInt10(viteEnv['VITE_SESSION_TIMEOUT_MINUTES'], 60),
        // CORS — browser doesn't enforce CORS origins server-side; keep default
        corsOrigins: parseStringArray(viteEnv['VITE_CORS_ORIGINS'], ['http://localhost:5173']),
        // Rate limiting — not enforced by browser; satisfy schema with defaults
        rateLimitMax: parseInt10(viteEnv['VITE_RATE_LIMIT_MAX'], 100),
        rateLimitWindowMs: parseInt10(viteEnv['VITE_RATE_LIMIT_WINDOW_MS'], 60000),
        // Logging
        logLevel: viteEnv['VITE_LOG_LEVEL'] || 'info',
        // R2 — not meaningful in browser; satisfy schema with defaults
        r2AccountId: undefined,
        r2AccessKeyId: undefined,
        r2SecretAccessKey: undefined,
        r2BucketName: 'kantera-uploads',
        r2SseCKey: undefined,
        // Postmark — not meaningful in browser
        postmarkApiToken: undefined,
        postmarkFromEmail: DEFAULT_POSTMARK_FROM_EMAIL,
        postmarkWebhookSecret: undefined,
        // Expo Push — not meaningful in browser; secret must never reach the client
        expoPushAccessToken: undefined,
        // Web Push (VAPID) — public key is safe to expose via VITE_; private key + subject stay server-side
        vapidPublicKey: viteEnv['VITE_VAPID_PUBLIC_KEY'],
        vapidPrivateKey: undefined,
        vapidSubject: undefined,
        // Twilio — server-only secrets, never exposed to the browser
        twilioAccountSid: undefined,
        twilioAuthToken: undefined,
        twilioPhoneNumber: undefined,
        // OpenWeatherMap — server-only
        openWeatherApiKey: undefined,
        // Anthropic — server-only secret, never exposed to the browser
        anthropicApiKey: undefined,
        anthropicModel: 'claude-sonnet-4-6',
        // Replicate — server-only secret, never exposed to the browser
        replicateApiToken: undefined,
        replicateYolo11ModelVersion: 'lucataco/yolo11:cc14833a98e8a58cc51ff2bb48c69c36e2b3ad40b90af5cfbf7a2c5b3bce4c22',
        replicatePaddleocrModelVersion: 'cjwbw/paddleocr:30f9069e56763e9fd43d67afac0a34e7c68e9b6b7f5d9f5e0c98f5c87c06ef5d',
        replicateAvatarModel: 'black-forest-labs/flux-1.1-pro',
        // Feature flags  (opt-in from VITE_ vars)
        featureVideoAnalysis: parseBool(viteEnv['VITE_FEATURE_VIDEO_ANALYSIS'], false),
        featureLiveScoring: parseBool(viteEnv['VITE_FEATURE_LIVE_SCORING'], false),
        featureGamification: parseBool(viteEnv['VITE_FEATURE_GAMIFICATION'], true),
        featureOfflineSync: parseBool(viteEnv['VITE_FEATURE_OFFLINE_SYNC'], false),
        // Upload limits
        maxUploadSizeMb: parseFloat10(viteEnv['VITE_MAX_UPLOAD_SIZE_MB'], 50),
        allowedImageTypes: parseStringArray(viteEnv['VITE_ALLOWED_IMAGE_TYPES'], [
            'image/jpeg',
            'image/png',
            'image/webp',
        ]),
        allowedVideoTypes: parseStringArray(viteEnv['VITE_ALLOWED_VIDEO_TYPES'], [
            'video/mp4',
            'video/quicktime',
        ]),
        // Pagination
        defaultPageSize: parseInt10(viteEnv['VITE_DEFAULT_PAGE_SIZE'], 20),
        maxPageSize: parseInt10(viteEnv['VITE_MAX_PAGE_SIZE'], 100),
        // Event-store snapshots
        snapshotThreshold: parseInt10(viteEnv['VITE_SNAPSHOT_THRESHOLD'], 50),
        // Club defaults — not configurable from the browser; satisfy schema with defaults
        defaultMaxTeams: 10,
        defaultMaxPlayers: 200,
        // Age verification — not configurable from the browser; satisfy schema with default
        ageVerificationExpiryDays: 730,
        // Sentry — VITE_SENTRY_DSN is intentionally public (it's a client-side DSN)
        sentryDsn: viteEnv['VITE_SENTRY_DSN'],
        sentryEnvironment: viteEnv['VITE_SENTRY_ENVIRONMENT'] || viteEnv['MODE'] || 'development',
        sentryTracesSampleRate: parseFloat10(viteEnv['VITE_SENTRY_TRACES_SAMPLE_RATE'], 0.1),
        // Stripe — secret keys are never exposed in the browser
        stripeSecretKey: undefined,
        stripeWebhookSecret: undefined,
        stripePriceIds: {
            community: 'price_community',
            club: 'price_club',
            academy: 'price_academy',
            professional: 'price_professional',
        },
    };
    return validateConfig(raw);
};
// ---------------------------------------------------------------------------
// Constants (unchanged — keep all existing exports so nothing breaks)
// ---------------------------------------------------------------------------
export const COPPA_MIN_AGE = 13;
/**
 * ML_TRAINING_ENABLED — controls whether the platform is permitted to use player
 * data for machine-learning model training.  Defaults to false.
 *
 * Set ENABLE_ML_TRAINING=true in the environment to opt in.
 * Requires explicit consent from all affected parents before enabling in production.
 */
export const ML_TRAINING_ENABLED = typeof process !== 'undefined' && process.env?.['ENABLE_ML_TRAINING'] === 'true';
/**
 * ML_TRAINING_ENABLED_WEB — browser-safe version that reads from Vite's
 * import.meta.env.  Use this in React / web code instead of ML_TRAINING_ENABLED.
 *
 * Set VITE_ML_TRAINING_ENABLED=true in .env to opt in.
 */
export function getMLTrainingEnabled() {
    // Server-side (Node.js)
    if (typeof process !== 'undefined' && process.env?.['ENABLE_ML_TRAINING'] !== undefined) {
        return process.env['ENABLE_ML_TRAINING'] === 'true';
    }
    // Client-side (Vite)
    try {
        const viteEnv = import.meta.env;
        return viteEnv?.['VITE_ML_TRAINING_ENABLED'] === 'true';
    }
    catch {
        return false;
    }
}
export const ROLES = {
    ADMIN: 'admin',
    COACH: 'coach',
    PARENT: 'parent',
    PLAYER: 'player',
};
export const POSITIONS = ['Goalkeeper', 'Defender', 'Midfielder', 'Forward'];
//# sourceMappingURL=index.js.map